import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["button", "panel", "messages", "input", "submitBtn", "loading", "welcome", "greetingBubble"]
  static values = { errorMessage: String }

  connect() {
    this.conversationId = null
    this.storageKey = 'cosmo_app_chat_history'
    this.greetingDismissedKey = 'cosmo_greeting_dismissed'
    this.messages = []
    // Get module from data attribute or default to ecommerce
    this.cosmoModule = this.element.dataset.cosmoModule || 'ecommerce'
    this.primaryColor = this.element.dataset.primaryColor || '#7094e0'
    this.autoOpen = this.element.dataset.autoOpen !== 'false'

    // Restore messages from localStorage
    this.restoreMessages()

    // The setup guide hides itself while this panel is open. When the user
    // clicks its pill they want the guide back, so get out of the way.
    this.closeRequestHandler = () => this.closeIfOpen()
    window.addEventListener("cosmo-close-request", this.closeRequestHandler)

    if (this.autoOpen && window.innerWidth > 480) {
      // Open widget immediately, auto-close if no interaction
      this.autoOpenAndScheduleClose()
    } else {
      // Show greeting bubble after delay
      this.scheduleGreeting()
    }
  }

  disconnect() {
    window.removeEventListener("cosmo-close-request", this.closeRequestHandler)
  }

  /**
   * Close the panel if it is open (no-op otherwise)
   */
  closeIfOpen() {
    const panel = this.element.querySelector('[data-chat-widget-target="panel"]')
    if (panel && !panel.classList.contains('hidden')) {
      this.toggle()
    }
    this.hideGreeting()
  }

  /**
   * Show greeting bubble after delay
   */
  scheduleGreeting() {
    // Don't show if already dismissed today or has existing messages
    const dismissed = localStorage.getItem(this.greetingDismissedKey)
    if (dismissed) {
      const dismissedDate = new Date(parseInt(dismissed))
      const now = new Date()
      if (now - dismissedDate < 24 * 60 * 60 * 1000) {
        return
      }
    }

    if (this.messages.length > 0) {
      return
    }

    this.greetingTimeout = setTimeout(() => {
      this.showGreeting()
    }, 30000)
  }

  /**
   * Open widget immediately and schedule auto-close if no interaction
   */
  autoOpenAndScheduleClose() {
    this.autoCloseDelay = 30000

    // Don't auto-open if dismissed in the last 24 hours
    const dismissed = localStorage.getItem(this.greetingDismissedKey)
    if (dismissed) {
      const dismissedDate = new Date(parseInt(dismissed))
      const now = new Date()
      if (now - dismissedDate < 24 * 60 * 60 * 1000) {
        return
      }
    }

    // Don't auto-open if user already has a conversation
    if (this.messages.length > 0) {
      return
    }

    // Open immediately
    this.toggle()

    // Schedule auto-close
    this.autoCloseTimeout = setTimeout(() => {
      const panel = this.element.querySelector('[data-chat-widget-target="panel"]')
      if (panel && !panel.classList.contains('hidden')) {
        this.toggle()
        localStorage.setItem(this.greetingDismissedKey, Date.now().toString())
      }
    }, this.autoCloseDelay)
  }

  /**
   * Cancel the auto-close timer (called on user interaction)
   */
  cancelAutoClose() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout)
      this.autoCloseTimeout = null
    }
  }

  /**
   * Show the greeting bubble
   */
  showGreeting() {
    // The greeting bubble sits in the same corner as the expanded setup guide
    // and would cover it. The guide wins - Cosmo still has its button.
    if (this.setupGuideExpanded()) {
      return
    }

    const bubble = this.element.querySelector('[data-chat-widget-target="greetingBubble"]')
    if (bubble) {
      bubble.classList.remove('hidden')
    }
  }

  /**
   * True while the setup guide card (not its collapsed pill) is on screen
   */
  setupGuideExpanded() {
    const card = document.getElementById('onboarding-guide-card')
    return !!card && card.getBoundingClientRect().height > 0
  }

  /**
   * Hide the greeting bubble
   */
  hideGreeting() {
    const bubble = this.element.querySelector('[data-chat-widget-target="greetingBubble"]')
    if (bubble) {
      bubble.classList.add('hidden')
    }
  }

  /**
   * Dismiss greeting and remember it
   */
  dismissGreeting(event) {
    event.stopPropagation()
    this.hideGreeting()
    localStorage.setItem(this.greetingDismissedKey, Date.now().toString())
    if (this.greetingTimeout) {
      clearTimeout(this.greetingTimeout)
    }
  }

  /**
   * Open chat from greeting bubble click
   */
  openFromGreeting(event) {
    // Don't trigger if clicking the close button
    if (event.target.classList.contains('cosmo-greeting-close')) {
      return
    }
    this.hideGreeting()
    localStorage.setItem(this.greetingDismissedKey, Date.now().toString())
    this.toggle()
  }

  /**
   * Save messages to localStorage
   */
  saveMessages() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        messages: this.messages,
        conversationId: this.conversationId
      }))
    } catch (e) {
      // localStorage might be full or disabled
    }
  }

  /**
   * Restore messages from localStorage
   */
  restoreMessages() {
    try {
      const saved = localStorage.getItem(this.storageKey)
      if (saved) {
        const data = JSON.parse(saved)
        this.messages = data.messages || []
        this.conversationId = data.conversationId || null

        if (this.messages.length > 0) {
          // Hide welcome section
          const welcome = this.element.querySelector('[data-chat-widget-target="welcome"]')
          if (welcome) welcome.style.display = 'none'

          // Render saved messages
          const messagesContainer = this.element.querySelector('[data-chat-widget-target="messages"]')
          this.messages.forEach(msg => {
            this.renderMessage(msg.content, msg.role, messagesContainer)
          })
        }
      }
    } catch (e) {
      this.messages = []
    }
  }

  /**
   * Clear chat history
   */
  clearHistory() {
    this.messages = []
    this.conversationId = null
    localStorage.removeItem(this.storageKey)

    // Reset UI
    const messagesContainer = this.element.querySelector('[data-chat-widget-target="messages"]')
    const welcome = this.element.querySelector('[data-chat-widget-target="welcome"]')

    if (messagesContainer) {
      // Remove all message bubbles but keep the welcome div
      const messageBubbles = messagesContainer.querySelectorAll('.flex.justify-end, .flex.justify-start')
      messageBubbles.forEach(bubble => bubble.remove())

      // Show welcome again
      if (welcome) welcome.style.display = 'block'
    }
  }

  sendPredefinedMessage(event) {
    event.preventDefault()
    this.cancelAutoClose()
    const message = event.currentTarget.dataset.message

    // Find and manually set the input value, then trigger sendMessage
    const input = this.element.querySelector('[data-chat-widget-target="input"]')
    if (input) {
      input.value = message

      // Create and dispatch a submit event on the form
      const form = input.closest('form')
      if (form) {
        // Hide welcome section
        const welcome = this.element.querySelector('[data-chat-widget-target="welcome"]')
        if (welcome) welcome.style.display = 'none'

        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
      }
    }
  }

  toggle(event) {
    if (event) event.preventDefault()
    console.log('Toggle called')

    // Manual fallback if Stimulus targets don't work
    const panel = this.element.querySelector('[data-chat-widget-target="panel"]')
    const input = this.element.querySelector('[data-chat-widget-target="input"]')

    console.log('Found panel manually:', !!panel)

    if (!panel) {
      console.error('Panel not found even manually')
      return
    }

    const isHidden = panel.classList.contains('hidden')
    console.log('Panel is hidden:', isHidden)

    if (isHidden) {
      panel.classList.remove('hidden')
      console.log('Panel should now be visible')
      if (input) {
        setTimeout(() => input.focus(), 100)
      }
    } else {
      panel.classList.add('hidden')
      console.log('Panel should now be hidden')
      this.cancelAutoClose()
      localStorage.setItem(this.greetingDismissedKey, Date.now().toString())
    }
  }

  async sendMessage(event) {
    event.preventDefault()
    this.cancelAutoClose()

    // Manual element selection as fallback
    const input = this.element.querySelector('[data-chat-widget-target="input"]')
    const submitBtn = this.element.querySelector('[data-chat-widget-target="submitBtn"]')
    const loading = this.element.querySelector('[data-chat-widget-target="loading"]')
    const messages = this.element.querySelector('[data-chat-widget-target="messages"]')
    const welcome = this.element.querySelector('[data-chat-widget-target="welcome"]')

    const message = input.value.trim()
    if (!message) return

    // Hide welcome section once user starts chatting
    if (welcome) welcome.style.display = 'none'

    // Disable input while processing
    input.disabled = true
    submitBtn.disabled = true

    // Add user message to UI
    this.addMessage(message, 'user', messages)
    input.value = ''

    // Show loading indicator
    if (loading) loading.classList.remove('hidden')

    try {
      const response = await fetch('/chats/widget_message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ message: message, cosmo_module: this.cosmoModule })
      })

      const data = await response.json()

      if (response.ok) {
        // Store conversation ID for future reference
        this.conversationId = data.conversation_id

        // Check if context was exceeded
        if (data.context_exceeded) {
          // Show the message first
          this.renderMessage(data.assistant_message, 'assistant', messages)
          // Then clear after delay so user sees the message
          setTimeout(() => {
            this.clearHistory()
          }, 2500)
        } else {
          // Normal flow - add assistant response to UI
          this.addMessage(data.assistant_message, 'assistant', messages)
        }
      } else {
        this.addMessage(this.errorMessageValue || 'Sorry, I encountered an error. Please try again.', 'assistant', messages)
      }
    } catch (error) {
      console.error('Error sending message:', error)
      this.addMessage(this.errorMessageValue || 'Sorry, I encountered an error. Please try again.', 'assistant', messages)
    } finally {
      // Hide loading indicator and re-enable input
      if (loading) loading.classList.add('hidden')
      input.disabled = false
      submitBtn.disabled = false
      input.focus()
    }
  }

  addMessage(content, role, messagesContainer) {
    // Save to messages array and localStorage
    this.messages.push({ content, role, timestamp: Date.now() })
    this.saveMessages()

    // Render the message
    this.renderMessage(content, role, messagesContainer)
  }

  renderMessage(content, role, messagesContainer) {
    if (!messagesContainer) {
      messagesContainer = this.element.querySelector('[data-chat-widget-target="messages"]')
    }

    const messageDiv = document.createElement('div')
    messageDiv.className = role === 'user'
      ? 'flex justify-end'
      : 'flex justify-start'

    const bubbleDiv = document.createElement('div')
    if (role === 'user') {
      bubbleDiv.className = 'text-white rounded-lg px-3 py-2 max-w-[80%] text-sm'
      bubbleDiv.style.backgroundColor = this.primaryColor
    } else {
      bubbleDiv.className = 'bg-white text-gray-900 rounded-lg px-3 py-2 max-w-[80%] text-sm shadow-sm border border-gray-200'
    }

    const textP = document.createElement('div')
    textP.className = 'whitespace-pre-wrap cosmo-message-content'

    // Parse markdown links and convert to HTML for assistant messages
    // User messages remain as plain text for security
    if (role === 'assistant') {
      textP.innerHTML = this.parseMarkdownToHTML(content)
    } else {
      textP.textContent = content
    }

    bubbleDiv.appendChild(textP)
    messageDiv.appendChild(bubbleDiv)

    messagesContainer.appendChild(messageDiv)

    // Scroll to bottom
    messagesContainer.scrollTop = messagesContainer.scrollHeight
  }

  parseMarkdownToHTML(text) {
    if (!text) return ''

    // Escape HTML to prevent XSS attacks
    let escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

    // Convert markdown links: [text](url) to HTML <a> tags
    // Only allow relative URLs starting with / and http/https absolute URLs
    escaped = escaped.replace(
      /\[([^\]]+)\]\(([^\)]+)\)/g,
      (_match, text, url) => {
        // Sanitize URL - only allow relative paths and http(s) URLs
        const sanitizedUrl = this.sanitizeUrl(url.trim())
        if (sanitizedUrl) {
          return `<a href="${sanitizedUrl}" class="cosmo-link" target="_blank" rel="noopener noreferrer">${text}</a>`
        }
        // If URL is not safe, return the text without link
        return text
      }
    )

    // Convert **bold** to <strong>
    escaped = escaped.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>')

    // Convert *italic* to <em>
    escaped = escaped.replace(/\*([^\*]+)\*/g, '<em>$1</em>')

    // Convert line breaks to <br> but preserve paragraph spacing
    escaped = escaped.replace(/\n\n/g, '</p><p>')
    escaped = escaped.replace(/\n/g, '<br>')

    // Wrap in paragraph if it contains breaks
    if (escaped.includes('<br>') || escaped.includes('</p>')) {
      escaped = '<p>' + escaped + '</p>'
    }

    return escaped
  }

  sanitizeUrl(url) {
    // Remove any whitespace
    url = url.trim()

    // Allow relative URLs starting with /
    if (url.startsWith('/')) {
      // Extra check: ensure no javascript: or data: schemes hidden in the path
      if (url.match(/javascript:/i) || url.match(/data:/i)) {
        return null
      }
      return url
    }

    // Allow http and https URLs
    if (url.match(/^https?:\/\//i)) {
      return url
    }

    // Reject anything else (javascript:, data:, file:, etc.)
    return null
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]').getAttribute('content')
  }
}
